<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { push } from 'svelte-spa-router';
  import { getMatches } from '../../lib/apiService';
  import type { Match } from '../../types';
  import { formatDate } from '../../lib/utils/reportUtils';

  export let target: 'preparation' | 'report' = 'report';
  export let scope: 'future' | 'past' = 'future';

  let matches: Match[] = [];
  let primaryMatches: Match[] = [];
  let otherMatches: Match[] = [];
  let loading = true;
  
  function filterAndSortMatches(allMatches: Match[]) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (scope === 'future') {
      // List 1: 2 days before today to 2 months in future, sorted ASC
      const threeDaysAgo = new Date(today);
      threeDaysAgo.setDate(today.getDate() - 2);
      const twoMonthsFuture = new Date(today);
      twoMonthsFuture.setMonth(today.getMonth() + 2);

      primaryMatches = allMatches
        .filter(match => {
          const matchDate = new Date(match.match_date);
          return matchDate >= threeDaysAgo && matchDate <= twoMonthsFuture;
        })
        .sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime());

      // List 2: All matches, sorted ASC
      otherMatches = allMatches
        .filter(match => {
          const matchDate = new Date(match.match_date);
          return matchDate < threeDaysAgo || matchDate > twoMonthsFuture;
        })
        .sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime());
    } else {
      // scope === 'past'
      // List 1: 3 weeks ago to today, sorted DESC
      const threeWeeksAgo = new Date(today);
      threeWeeksAgo.setDate(today.getDate() - 21);

      primaryMatches = allMatches
        .filter(match => {
          const matchDate = new Date(match.match_date);
          return matchDate >= threeWeeksAgo && matchDate <= today;
        })
        .sort((a, b) => new Date(b.match_date).getTime() - new Date(a.match_date).getTime());

      // List 2: All other matches, sorted ASC
      otherMatches = allMatches
        .filter(match => {
          const matchDate = new Date(match.match_date);
          return matchDate < threeWeeksAgo || matchDate > today;
        })
        .sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime());
    }
  }

  async function loadMatches() {
    try {
      loading = true;
      await getMatches((result, status) => {
        if (status === 200) {
          // Filter out matches without required data
          matches = result.filter(match => match.match_date && match.opponent_club);
          filterAndSortMatches(matches);
        }
        loading = false;
      });
    } catch (error) {
      console.error('Error loading matches:', error);
      loading = false;
    }
  }

  function handleMatchClick(match: Match) {
    if (target === 'preparation') {
      push(`/match/${match.id}/details`);
    } else if (target === 'report') {
      push(`/match/${match.id}/report`);
    } else if (target === 'highlights') {
      push(`/match/${match.id}/highlights`);
    }
  }


  function formatScore(match: Match): string {
    // Always show home team first in the results
    const homeScore = match.location === 'home' ? match.score_team : match.score_opponent;
    const awayScore = match.location === 'home' ? match.score_opponent : match.score_team;
    
    if (homeScore !== undefined && awayScore !== undefined) {
      return `${homeScore} - ${awayScore}`;
    }
    return '-';
  }

  function getLocationText(match: Match): string {
    return match.location === 'home' ? $_('labels.home') : $_('labels.away');
  }

  function getOpponentLogoUrl(match: Match): string {
    if (match.opponent_club?.logo_filename) {
      // Assuming logo URLs are served from a specific endpoint
      return `/img/clublogos/${match.opponent_club.logo_filename}?${import.meta.env.VITE_CACHE_VERSION}`;
    }
    return `/img/clublogos/default.png`;
  }

  onMount(() => {
    loadMatches();
  });
</script>

<div class="match-list-page">
  <div class="page-header">
    <h1>
      {#if target === 'preparation'}
        {$_('matchList.selectMatchPreparation')}
      {:else if target === 'report'}
        {$_('matchList.selectMatchReport')}
      {:else if target === 'highlights'}
        {$_('matchList.selectMatchHighlights')}
      {/if}
    </h1>
    <p class="subtitle">
      {#if target === 'preparation'}
        {$_('matchList.preparationSubtitle')}
      {:else if target === 'report'}
        {$_('matchList.reportSubtitle')}
      {:else if target === 'highlights'}
        {$_('matchList.highlightsSubtitle')}
      {/if}
    </p>
  </div>

  {#if loading}
    <div class="loading-state">
      <p>{$_('common.loading')}</p>
    </div>
  {:else if primaryMatches.length > 0 || otherMatches.length > 0}
    <!-- Primary matches list -->
    {#if primaryMatches.length > 0}
      <div class="matches-container">
        {#each primaryMatches as match (match.id)}
          <div 
            class="match-row clickable"
            on:click={() => handleMatchClick(match)}
            on:keydown={(e) => e.key === 'Enter' && handleMatchClick(match)}
            tabindex="0"
            role="button"
          >
            <div class="match-info">
              <div class="opponent-info">
                {#if getOpponentLogoUrl(match)}
                  <img 
                    src={getOpponentLogoUrl(match)} 
                    alt="{match.opponent_club?.name} logo"
                    class="team-logo-small"
                    on:error={(e) => e.target.style.display = 'none'}
                  />
                {:else}
                  <div class="team-logo-placeholder"></div>
                {/if}
                <div class="opponent-details">
                  <div class="opponent-name">
                    {match.opponent_club?.name || $_('common.unknown')}
                  </div>
                  <div class="opponent-team">
                    {match.opponent_team_name}
                  </div>
                </div>
              </div>
              
              <div class="match-details">
                <div class="match-date">
                  {formatDate(match.match_date)}
                </div>
                <div class="location-indicator">
                  {getLocationText(match)}
                </div>
              </div>
              
              <div class="match-result">
                <div class="score">
                  {formatScore(match)}
                </div>
              </div>
            </div>
            
            <div class="action-arrow">
              <ion-icon name="chevron-forward"></ion-icon>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Other matches section -->
    {#if otherMatches.length > 0}
      <div class="other-matches-section">
        <h3 class="other-matches-heading">{$_('matchList.otherMatches')}</h3>
        <div class="matches-container">
          {#each otherMatches as match (match.id)}
            <div 
              class="match-row clickable"
              on:click={() => handleMatchClick(match)}
              on:keydown={(e) => e.key === 'Enter' && handleMatchClick(match)}
              tabindex="0"
              role="button"
            >
              <div class="match-info">
                <div class="opponent-info">
                  {#if getOpponentLogoUrl(match)}
                    <img 
                      src={getOpponentLogoUrl(match)} 
                      alt="{match.opponent_club?.name} logo"
                      class="team-logo-small"
                      on:error={(e) => e.target.style.display = 'none'}
                    />
                  {:else}
                    <div class="team-logo-placeholder"></div>
                  {/if}
                  <div class="opponent-details">
                    <div class="opponent-name">
                      {match.opponent_club?.name || $_('common.unknown')}
                    </div>
                    <div class="opponent-team">
                      {match.opponent_team_name}
                    </div>
                  </div>
                </div>
                
                <div class="match-details">
                  <div class="match-date">
                    {formatDate(match.match_date)}
                  </div>
                  <div class="location-indicator">
                    {getLocationText(match)}
                  </div>
                </div>
                
                <div class="match-result">
                  <div class="score">
                    {formatScore(match)}
                  </div>
                </div>
              </div>
              
              <div class="action-arrow">
                <ion-icon name="chevron-forward"></ion-icon>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  {:else}
    <div class="empty-state">
      <p>
        {#if target === 'preparation'}
          {$_('matchList.noMatchesPreparation')}
        {:else}
          {$_('matchList.noMatchesReport')}
        {/if}
      </p>
    </div>
  {/if}
</div>

<style>
  .match-list-page {
    padding: 1rem;
    max-width: 800px;
    margin: 0 auto;
  }

  .page-header {
    margin-bottom: 2rem;
    text-align: center;
  }

  .page-header h1 {
    margin-bottom: 0.5rem;
  }

  .subtitle {
    color: var(--pico-muted-color);
    margin: 0;
  }

  .loading-state, .empty-state {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--pico-muted-color);
  }

  .matches-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .match-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border: 1px solid var(--pico-border-color);
    border-radius: var(--pico-border-radius);
    background: var(--pico-card-background-color);
    transition: all 0.2s ease;
  }

  .match-row:hover {
    border-color: var(--pico-primary);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .match-row:focus {
    outline: 2px solid var(--pico-primary);
    outline-offset: 2px;
  }

  .match-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
  }

  .opponent-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    flex: 1;
  }

  .team-logo-small {
    width: 40px;
    height: 40px;
    object-fit: contain;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .team-logo-placeholder {
    width: 40px;
    height: 40px;
    background: var(--pico-muted-border-color);
    border-radius: 4px;
    flex-shrink: 0;
  }

  .opponent-details {
    min-width: 0;
    flex: 1;
  }

  .opponent-name {
    font-weight: 600;
    margin-bottom: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .opponent-team {
    font-size: 0.875rem;
    color: var(--pico-muted-color);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .match-details {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    text-align: center;
    min-width: 80px;
  }

  .match-date {
    font-size: 0.875rem;
    font-weight: 500;
  }

  .location-indicator {
    font-size: 0.75rem;
    color: var(--pico-muted-color);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .match-result {
    text-align: center;
    min-width: 60px;
  }

  .score {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--pico-contrast);
  }

  .action-arrow {
    color: var(--pico-muted-color);
    margin-left: 1rem;
  }

  .other-matches-section {
    margin-top: 2rem;
  }

  .other-matches-heading {
    margin-bottom: 1rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--pico-contrast);
    padding-left: 0.5rem;
  }

  /* Mobile responsive */
  @media (max-width: 640px) {
    .match-row {
      padding: 0.75rem;
    }

    .match-info {
      gap: 0.75rem;
    }

    .opponent-info {
      gap: 0.5rem;
    }

    .team-logo-small, .team-logo-placeholder {
      width: 32px;
      height: 32px;
    }

    .match-details {
      min-width: 70px;
    }

    .match-result {
      min-width: 50px;
    }

    .score {
      font-size: 1.125rem;
    }
  }
</style>